import {GetEntries, ModifyString, Timer} from "js-vextensions";
import React, {useCallback, useEffect} from "react";
import {GetVoices, InfoButton, Observer, RunInAction_Set, TextPlus} from "web-vcore";
import {Button, CheckBox, Column, Row, Select, Spinner, Text, TextInput} from "react-vcomponents";
import {BaseComponent, BaseComponentPlus} from "react-vextensions";
import {store} from "../../../../Store/index.js";
import {InAndroid, nativeBridge} from "../../../../Utils/Bridge/Bridge_Native.js";
import {AudioSettingsSubpanel} from "../../../../Store/main/settings/audio.js";
import {GetSelectedEngineConfig} from "../../../../Server/DB/engineConfigs.js";
import {ScrollView} from "react-vscrollview";
import {PlaySound, PlaySound_StopAll} from "../../../../Utils/Bridge/Bridge_Native/MediaPlayer.js";
import {GetAudioFilesWithSubpath, GetCache} from "../../../../Store/main/general/CacheState.js";
import {SpinnerForPercent} from "../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {observer_mgl} from "mobx-graphlink";

export const SettingsUI_Audio = observer_mgl(()=>{
	const uiState = store.main.settings.audio;
	return (
		<>
			<Row mt={5}>
				<Select displayType="button bar" options={GetEntries(AudioSettingsSubpanel, "ui")} value={uiState.subpanel} onChange={val=>uiState.subpanel = val}/>
				{uiState.subpanel == AudioSettingsSubpanel.files &&
				<Row ml="auto">
					<TextInput placeholder="Filter..." value={uiState.audioFilesFilter} onChange={val=>RunInAction_Set(()=>uiState.audioFilesFilter = val)}/>
					<Button ml={5} mdIcon="refresh" onClick={()=>{
						RunInAction_Set(()=>GetCache().audioFiles_updatedRequestedAt = Date.now());
					}}/>
					<Button ml={5} mdIcon="stop" onClick={()=>{
						PlaySound_StopAll();
					}}/>
				</Row>}
			</Row>
			{uiState.subpanel == AudioSettingsSubpanel.config && <ConfigSubpanel/>}
			{uiState.subpanel == AudioSettingsSubpanel.files && <FilesSubpanel/>}
		</>
	);
});

class SystemVolumes {
	media = 0;
	call = 0;
	ringtone = 0;
	// guesses, until data comes in
	media_step = .05;
	call_step = .05;
	ringtone_step = .05;
};

const ConfigSubpanel = observer_mgl(()=>{
	const [devices, setDevices] = React.useState<MediaDeviceInfo[]>([]);
	const [systemVolumes, setSystemVolumes] = React.useState(new SystemVolumes());
	const uiState = store.main.settings.audio;

	const refreshDevices = useCallback(async()=>{
		const devices = navigator.mediaDevices ? await navigator.mediaDevices.enumerateDevices() : [];
		setDevices(devices);
	}, [setDevices]);
	const refreshVolumes = useCallback(async()=>{
		const volumes = (await nativeBridge.Call("GetSystemVolumes")) as SystemVolumes;
		setSystemVolumes(volumes);
	}, [setSystemVolumes]);

	useEffect(()=>{
		refreshDevices();
		refreshVolumes();
		const timer = new Timer(2500, ()=>{
			refreshVolumes();
		}).Start();
		return ()=>timer.Stop();
	}, [refreshDevices, refreshVolumes]);

	const microphoneOptions = ([] as {name: string, value: string|null}[])
		.concat({name: "(computer default)", value: null})
		.concat(devices.filter(a=>a.kind == "audioinput").map((device, index)=>({name: `${device.label} [${index}]`, value: device.deviceId})));
	const voiceOptions = [{name: "", value: null} as any].concat(GetVoices().map(a=>a.name));

	return (
		<>
			<Row mt={5}>
				<Text>Main microphone:</Text>
				<Select ml={5} options={microphoneOptions} value={uiState.mainMicrophoneID} onChange={val=>RunInAction_Set(()=>uiState.mainMicrophoneID = val)}/>
			</Row>
			<Row center mt={5}>
				<Text>Main voice:</Text>
				<Select ml={5} options={voiceOptions} value={uiState.mainVoiceName} onChange={val=>RunInAction_Set(()=>uiState.mainVoiceName = val)}/>
				<InfoButton ml={5} text={`
					This voice is used whenever the target voice is unspecified or missing. (eg. for sound-effects created by others)

					Important note: The Google voices appear to have a duration limit! If for long text (eg. dream narration), use the native/offline voices instead.
				`.AsMultiline(0)}/>
			</Row>
			<Row center mt={5}>
				<Text>Voice speed multiplier:</Text>
				<SpinnerForPercent ml={5} value={uiState.voiceSpeedMultiplier} onChange={val=>RunInAction_Set(()=>uiState.voiceSpeedMultiplier = val)}/>
				<InfoButton ml={5} text={`Useful in case this device's text-to-speech engine talks faster/slower than that of your other device(s).`}/>
			</Row>
			<Row center mt={5}>
				<CheckBox text="Use ringtone channel for voice." value={uiState.voiceOnRingtoneChannel} onChange={val=>RunInAction_Set(()=>uiState.voiceOnRingtoneChannel = val)}/>
			</Row>

			{InAndroid(1) &&
				<>
					<Row mt={10}>{`Sessions: System start volume -> current volume`}</Row>
					<Column mt={5} ml={15}>
						<VolumeChannelRow channel="media" systemVolumes={systemVolumes} refreshVolumes={refreshVolumes}/>
						<VolumeChannelRow channel="call" systemVolumes={systemVolumes} refreshVolumes={refreshVolumes}/>
						<VolumeChannelRow channel="ringtone" systemVolumes={systemVolumes} refreshVolumes={refreshVolumes}/>
					</Column>
				</>}

			<Row mt={10}>
				<TextPlus info={loudnessCurveHelp}>Loudness curves:</TextPlus>
				<Text ml={5}>MediaPlayer:</Text>
				<Spinner ml={5} min={0} max={10} step={.1} value={uiState.loudnessCurve_mediaPlayer} onChange={val=>RunInAction_Set(()=>uiState.loudnessCurve_mediaPlayer = val)}/>
				<Text ml={5}>SystemTTS:</Text>
				<Spinner ml={5} min={0} max={10} step={.1} value={uiState.loudnessCurve_systemTTS} onChange={val=>RunInAction_Set(()=>uiState.loudnessCurve_systemTTS = val)}/>
			</Row>
		</>
	);
});

const VolumeChannelRow = observer_mgl((props: {channel: "media" | "call" | "ringtone", systemVolumes: SystemVolumes, refreshVolumes: ()=>any})=>{
	const {channel, systemVolumes, refreshVolumes} = props;
	const currentVolume = systemVolumes[channel] as number;
	const uiState = store.main.settings.audio;
	const channel_cap = ModifyString(channel, m=>[m.startLower_to_upper]);

	/*const text =
		channel == "media" ? `Media (eg. voice channel opt-1):` :
		channel == "call" ? `Call (eg. yt videos):` :
		`Ringtone (eg. voice channel opt-2):`;*/
	const text = `${channel_cap}:`;
	const info =
		channel == "media" ? `Note: Media stream is used for the web-based synthesized sounds; and the TTS voice, if "Use ringtone channel for voice" is disabled.` :
		channel == "call" ? `Note: Call stream is used for youtube videos, for some reason. (on my old phone anyway)` :
		`Note: Ringtone channel is used for the TTS voice, if "Use ringtone channel for voice" is enabled.`;

	return (
		<Row center mt={5}>
			<CheckBox text={text} style={{width: 80}}
				value={uiState[`sessions_systemStartVolume_${channel}_enabled`]} onChange={val=>RunInAction_Set(()=>uiState[`sessions_systemStartVolume_${channel}_enabled`] = val)}/>
			<Spinner ml={5} min={0} max={100} enabled={uiState[`sessions_systemStartVolume_${channel}_enabled`]}
				value={(uiState[`sessions_systemStartVolume_${channel}`] * 100).RoundTo(1)} onChange={val=>RunInAction_Set(()=>uiState[`sessions_systemStartVolume_${channel}`] = val / 100)}/>
			<Text>%</Text>
			<InfoButton ml={5} text={info}/>
			<Button ml={5} mdIcon="arrow-right-thick" onClick={async()=>{
				await nativeBridge.Call(`SetSystemVolume_${channel_cap}`, uiState[`sessions_systemStartVolume_${channel}`]);
				refreshVolumes();
			}}/>
			<SpinnerForPercent style={{marginLeft: 5}} value={currentVolume} onChange={async val=>{
				await nativeBridge.Call(`SetSystemVolume_${channel_cap}`, val);
				refreshVolumes();
			}}/>
			<Button ml={5} size={36} mdIcon="minus-circle" onClick={async()=>{
				const step = systemVolumes[`${channel}_step`];
				await nativeBridge.Call(`SetSystemVolume_${channel_cap}`, (currentVolume - step).KeepBetween(0, 1));
				refreshVolumes();
			}}/>
			<Button ml={5} size={36} mdIcon="plus-circle" onClick={async()=>{
				const step = systemVolumes[`${channel}_step`];
				await nativeBridge.Call(`SetSystemVolume_${channel_cap}`, (currentVolume + step).KeepBetween(0, 1));
				refreshVolumes();
			}}/>
		</Row>
	);
});

export const loudnessCurveHelp = `
	Many audio APIs have a "volume" parameter that actually represents energy (eg. loudness^2) rather than perceptual-loudness.
	Fix: We apply a transformation, of Math.pow(volume, curve), just before sending a volume (ie. loudness) value to the API.
	Result: A linear increase in the volume value, results in a linear increase in the perceived loudness.

	Determining the curve:
	1 = make no adjustment (ie. the api's volume parameter already represents perceptual-loudness)
	<1 = use if perceptual-loudness was "changing faster" near 100% (rare, if ever, that an api/device actually does this!)
	>1 = use if perceptual-loudness was "changing faster" near 0% (common; for this case, a value of 2 is generally used)
	See: https://ux.stackexchange.com/a/116300
`.AsMultiline(0);

const FilesSubpanel = observer_mgl(()=>{
	const uiState = store.main.settings.audio;
	const engineConfig = GetSelectedEngineConfig();

	return (
		<>
			<ScrollView>
				{GetAudioFilesWithSubpath(uiState.audioFilesFilter).map((file, index)=>{
					return (
						<Row key={index} mt={5}>
							<Row style={{width: "calc(100% - 70px)"}}>
								<Text style={{wordBreak: "break-word"}}>{file.filePath}</Text>
								<InfoButton ml={5} text={`ContentUri: ${file.contentUri}`}/>
							</Row>
							<Row ml="auto" center>
								<Button mdIcon="play" onClick={async()=>{
									// todo: see if there's a better way to set defaults than by reading from the dream-quiz comp
									PlaySound({entry: file}, {volume: engineConfig?.dreamQuiz?.soundVolume ?? 1, durationLimit: engineConfig?.dreamQuiz?.soundDurationLimit});
								}}/>
								<InfoButton ml={5} text="Note that the play button uses the volume and duration-limit settings from the selected engine-config's dream-quiz section. (if set)"/>
							</Row>
						</Row>
					);
				})}
			</ScrollView>
		</>
	);
});